import React, { useContext, useState } from "react";
import { styled } from "@mui/system";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { ChangeAllBucketsModalContext } from "./ChangeAllBucketsModalContext";
import useDeedSuggestions from "../../../hooks/useDeedSuggestions";
import useDeeds from "../../../hooks/useDeeds";
import { useInvalidateDeeds } from "../../../hooks/useDeedMutations";
import updateAllBuckets from "./updateAllBuckets";

// Customized Dialog appearance
const StyledDialogPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: theme.shadows[6],
  paddingBottom: "0px",
  paddingTop: "10px",
  [theme.breakpoints.up("xs")]: {
    minWidth: "90%",
  },
  [theme.breakpoints.up("sm")]: {
    minWidth: "444px",
  },
}));

function ChangeAllBucketsModal() {
  const { isChangeAllBucketsModalOpen, toggleChangeAllBucketsModal } =
    useContext(ChangeAllBucketsModalContext);

  const { sortedDeeds } = useDeeds();
  const { bucketOptions } = useDeedSuggestions();
  const invalidateDeeds = useInvalidateDeeds();

  const [bucket, setBucket] = useState("");
  const [newBucketName, setNewBucketName] = useState("");

  const [showBucketError, setShowBucketError] = useState(false);
  const [showNewBucketNameError, setShowNewBucketNameError] = useState(false);

  // number of deeds with matching bucket (purely a preview — the server does
  // the actual rename atomically).
  function countDeedInstances(sortedDeeds, bucket) {
    if (!Array.isArray(sortedDeeds) || !bucket) return 0;
    return sortedDeeds.reduce(
      (count, deed) => (deed.bucket === bucket ? count + 1 : count),
      0
    );
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (bucket && newBucketName) {
      try {
        await updateAllBuckets(bucket, newBucketName);
        invalidateDeeds();
        toggleChangeAllBucketsModal();
      } catch (error) {
        console.error("Failed to rename bucket:", error);
      }
    } else {
      if (!bucket) setShowBucketError(true);
      if (!newBucketName) setShowNewBucketNameError(true);
    }
  };

  return (
    <Dialog
      open={isChangeAllBucketsModalOpen}
      onClose={() => {
        toggleChangeAllBucketsModal();
        setShowBucketError(false);
        setShowNewBucketNameError(false);
      }}
      PaperComponent={StyledDialogPaper} // use your styled component here
    >
      <DialogTitle>
        <Grid
          container
          spacing={2}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Grid item xs={6}>
            Rename Bucket
          </Grid>
          <Grid item xs={6} align="right" marginTop="-32px" marginRight="-18px">
            <IconButton onClick={toggleChangeAllBucketsModal}>
              <CloseRoundedIcon />
            </IconButton>{" "}
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent>
        <form onSubmit={handleSubmit}>
          <Box mt={2}>
            <Autocomplete
              variant="outlined"
              size="small"
              selectOnFocus
              clearOnBlur
              id="bucket-autocomplete"
              freeSolo
              options={bucketOptions}
              getOptionLabel={(option) => option}
              value={bucket}
              onChange={(event, newValue) => {
                setBucket(newValue);
                if (newValue !== "") {
                  setShowBucketError(false);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Existing bucket"
                  variant="outlined"
                  error={showBucketError}
                  helperText={showBucketError && "This field is required."}
                />
              )}
              required
            />
          </Box>
          <Box mt={2}>
            <TextField
              id="new-bucket-name"
              label="New bucket name"
              value={newBucketName}
              onChange={(event) => {
                setNewBucketName(event.target.value);
                if (event.target.value !== "") {
                  setShowNewBucketNameError(false);
                }
              }}
              variant="outlined"
              size="small"
              fullWidth
              error={showNewBucketNameError}
              helperText={showNewBucketNameError && "This field is required."}
            />
          </Box>
          <DialogActions sx={{ px: 0, pt: 0, pb: 0.5 }}>
            <Grid container spacing={2} justifyContent={"space-between"} mt={1}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  style={{ width: "100%" }} // This makes the button take up all the available space
                  onClick={toggleChangeAllBucketsModal}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  style={{ width: "100%" }} // This makes the button take up all the available space
                  type="submit"
                >
                  {" "}
                  Save
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
          <Fade in={countDeedInstances(sortedDeeds, bucket) > 0}>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign={"right"}
            >
              <span style={{ fontWeight: "bold" }}>
                {countDeedInstances(sortedDeeds, bucket)}
              </span>{" "}
              entries will be updated.
            </Typography>
          </Fade>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ChangeAllBucketsModal;
